import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useMemo, useState } from 'react';
import { ZoomSeatCapacityAlert } from '@/components/ZoomSeatCapacityAlert';
import { useZoomSeatUsage } from '@/hooks/useZoomSeatUsage';
import { rolesNeedZoomSeat, zoomSeatsBlockSubmit } from '@/schemas/zoom.schema';
import {
  collapseRolesForDisplay,
  derivePrimaryRole,
  normalizeHeldRoles,
  removeDisplayRoleFromHeld,
  roleDisplayLabel,
  rolesAvailableToAdd,
} from '@unify/fhir-model/role-display';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { formatUsDateTimeWithZone } from '@/lib/unifyDateFormat';
import { removalWouldLeaveNoRoles } from '@/lib/roleTransitionPolicy';
import type { Practice, Role, User } from '@/pages/users/types/rbac';
import { FEATURE_VISIBILITY } from '@/config/featureVisibility';
import { RolePermissionsView } from './RolePermissionsView';
import { SupervisionCard } from './SupervisionCard';
import { useRoleAssignments } from '../hooks/useRoleAssignments';
import {
  useAccessPolicies,
  useProfileResourceForUser,
  useRolePermissionsViewModel,
} from '../hooks/useRolePermissionsData';

function RolePermissionsReferenceSection({
  userId,
  role,
  refreshToken = 0,
}: {
  userId: string;
  role: Role;
  refreshToken?: number;
}): JSX.Element {
  const { policies, loading: policiesLoading, error: policiesError } = useAccessPolicies();
  const { source: profileUiSource, loading: profileSourceLoading } = useProfileResourceForUser(
    userId,
    role,
    refreshToken,
  );
  const rolePermissionsViewModel = useRolePermissionsViewModel(role, policies, profileUiSource);

  return (
    <RolePermissionsView
      viewModel={rolePermissionsViewModel}
      loading={policiesLoading || profileSourceLoading}
      error={policiesError}
    />
  );
}

interface RoleRow {
  role: Role;
  label: string;
  isPrimary: boolean;
  assignedAt?: string;
  assignedBy: string;
  canRemove: boolean;
}

export interface UserPermissionsTabPanelProps {
  userId: string;
  /** Derived primary for shell / permissions reference. */
  role: Role;
  /** Full held functional role set (includes stacked provider when supervisor). */
  heldRoles: Role[];
  practiceId: string;
  canEditUserRole: boolean;
  showRolePermissionsReference: boolean;
  /** Roles the editor may assign (actor matrix). */
  assignableRoles: Role[];
  onHeldRolesChange: (next: Role[]) => void;
  /** True while a role write is in flight. */
  roleSaving?: boolean;
  /** Bumped by the parent after a role write to re-read supervision and assignment history. */
  refreshToken?: number;
  /** Login-default / first profile role extension (not the derived shell primary). */
  loginDefaultRole?: Role;
  /** Self only — persist login default among assigned roles. */
  onLoginDefaultChange?: (slug: string | null) => void;
  loginDefaultSaving?: boolean;
  isSelf?: boolean;
  practices: Practice[];
  allUsers: User[];
  canDeactivate?: boolean;
  isInactive?: boolean;
  onDeactivate?: () => void;
  onReactivate?: () => void;
  accountActionLoading?: boolean;
}

/** Assigned roles, supervision, and account status — same panel for other-user Permissions and Settings → Security. */
export function UserPermissionsTabPanel({
  userId,
  role,
  heldRoles,
  practiceId,
  canEditUserRole,
  showRolePermissionsReference,
  assignableRoles,
  onHeldRolesChange,
  roleSaving = false,
  refreshToken = 0,
  loginDefaultRole,
  onLoginDefaultChange,
  loginDefaultSaving = false,
  isSelf = false,
  practices,
  allUsers,
  canDeactivate = false,
  isInactive = false,
  onDeactivate,
  onReactivate,
  accountActionLoading = false,
}: UserPermissionsTabPanelProps): JSX.Element {
  const medplum = useMedplum();
  const [roleToAdd, setRoleToAdd] = useState<string | null>(null);
  const [addRoleOpen, setAddRoleOpen] = useState(false);

  const { assignments, loading: assignmentsLoading } = useRoleAssignments(userId, refreshToken);
  const displayRoles = useMemo(() => collapseRolesForDisplay(heldRoles), [heldRoles]);
  const addOptions = useMemo(
    () =>
      rolesAvailableToAdd(heldRoles, assignableRoles).map((r) => ({
        value: r,
        label: roleDisplayLabel(r),
      })),
    [heldRoles, assignableRoles],
  );
  const derivedPrimary = derivePrimaryRole(heldRoles) ?? role;
  const displayedPrimary =
    loginDefaultRole && displayRoles.some((r) => r === loginDefaultRole)
      ? loginDefaultRole
      : derivedPrimary;

  const handleAdd = () => {
    if (!roleToAdd || !canEditRoles) return;
    const next = normalizeHeldRoles([...heldRoles, roleToAdd]) as Role[];
    onHeldRolesChange(next);
    closeAddRoleModal();
  };

  const handleRemove = (displayRole: string) => {
    if (!canEditRoles) return;
    if (removalWouldLeaveNoRoles(heldRoles, displayRole)) return;
    onHeldRolesChange(removeDisplayRoleFromHeld(heldRoles, displayRole) as Role[]);
  };

  const closeAddRoleModal = () => {
    setAddRoleOpen(false);
    setRoleToAdd(null);
  };

  const canEditRoles = canEditUserRole && !isInactive;
  const hasAssignedRoles = displayRoles.length > 0;
  const showZoomSeatAlert = addRoleOpen && roleToAdd != null && rolesNeedZoomSeat([roleToAdd]);
  const zoomSeatUsage = useZoomSeatUsage(medplum, showZoomSeatAlert && canEditRoles);
  const zoomSeatsUnavailable =
    showZoomSeatAlert && zoomSeatsBlockSubmit(zoomSeatUsage.usage, zoomSeatUsage.loading);

  const canRemoveRole = (displayRole: string): boolean => {
    if (!canEditRoles) return false;
    if (displayRole === 'practice-owner') return false;
    if (removalWouldLeaveNoRoles(heldRoles, displayRole)) return false;
    return assignableRoles.includes(displayRole as Role) || assignableRoles.includes(role);
  };

  const resolveAssignedBy = (reference?: string, display?: string): string => {
    const id = reference?.startsWith('Practitioner/') ? reference.split('/')[1] : undefined;
    if (display && !reference?.startsWith('Bot/')) return display;
    if (!id) return '—';
    const match = allUsers.find((u) => u.id === id);
    return match?.name || match?.email || '—';
  };

  const roleRows: RoleRow[] = displayRoles.map((r) => {
    const assignment = assignments[r];
    return {
      role: r as Role,
      label: roleDisplayLabel(r),
      isPrimary: r === displayedPrimary,
      assignedAt: assignment?.assignedAt,
      assignedBy: resolveAssignedBy(assignment?.assignedByReference, assignment?.assignedByDisplay),
      canRemove: canRemoveRole(r),
    };
  });

  const roleColumns: UnifyTableColumn<RoleRow>[] = [
    {
      key: 'role',
      label: 'Role',
      render: (row) => (
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {row.label}
          </Text>
          {row.isPrimary ? (
            <Badge size="sm" variant="light">
              Primary
            </Badge>
          ) : null}
        </Group>
      ),
      getValue: (row) => row.label,
    },
    {
      key: 'assignedAt',
      label: 'Assigned',
      render: (row) => (
        <Text size="sm" c={row.assignedAt ? undefined : 'dimmed'}>
          {row.assignedAt ? formatUsDateTimeWithZone(row.assignedAt) : assignmentsLoading ? 'Loading…' : '—'}
        </Text>
      ),
      getValue: (row) => row.assignedAt ?? '',
    },
    {
      key: 'assignedBy',
      label: 'Assigned by',
      render: (row) => (
        <Text size="sm" c={row.assignedBy === '—' ? 'dimmed' : undefined}>
          {row.assignedBy}
        </Text>
      ),
      getValue: (row) => row.assignedBy,
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      pinRight: true,
      align: 'right',
      render: (row) =>
        row.canRemove ? (
          <Button
            size="xs"
            variant="light"
            color="red"
            loading={roleSaving}
            onClick={() => handleRemove(row.role)}
          >
            {row.role === 'supervisor' ? 'Change to Provider' : 'Remove'}
          </Button>
        ) : removalWouldLeaveNoRoles(heldRoles, row.role) && canEditRoles ? (
          <Tooltip label="Deactivate the user instead of removing their last role">
            <Text size="sm" c="dimmed" style={{ cursor: 'default' }}>
              —
            </Text>
          </Tooltip>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
      getValue: () => '',
    },
  ];

  return (
    <Stack gap="lg" w="100%" maw="100%">
      <UnifyTable
        columns={roleColumns}
        data={roleRows}
        getRowKey={(row) => row.role}
        emptyMessage="No roles assigned"
        tableProps={{ style: { tableLayout: 'fixed', width: '100%' } }}
        headerRight={
          canEditRoles && addOptions.length > 0 ? (
            <Button
              leftSection={<IconPlus size={16} />}
              color="gold"
              loading={roleSaving}
              onClick={() => setAddRoleOpen(true)}
            >
              Add role
            </Button>
          ) : undefined
        }
        title="Assigned roles"
      />
      {isSelf && displayRoles.length > 1 && onLoginDefaultChange ? (
        <Select
          label="My default role"
          description="Shown when you sign in. You can still switch roles during a session from the account menu."
          data={displayRoles.map((r) => ({ value: r, label: roleDisplayLabel(r) }))}
          value={displayedPrimary}
          onChange={onLoginDefaultChange}
          disabled={loginDefaultSaving}
        />
      ) : null}

      {heldRoles.includes('practice-owner') ? (
        <Alert color="blue">
          Practice ownership is transferred from Practice settings, not here.
        </Alert>
      ) : null}

      {role !== 'client' && (
        <SupervisionCard
          practitionerId={userId}
          practiceId={practiceId}
          practices={practices}
          allUsers={allUsers}
          refreshToken={refreshToken}
          isInactive={isInactive}
          heldRoles={heldRoles}
        />
      )}

      {showRolePermissionsReference && hasAssignedRoles && FEATURE_VISIBILITY.profileRolePermissions ? (
        <RolePermissionsReferenceSection
          userId={userId}
          role={derivedPrimary as Role}
          refreshToken={refreshToken}
        />
      ) : null}

      {canDeactivate ? (
        <Card withBorder>
          <Stack gap="md">
            <Title order={4}>Account status</Title>
            {isInactive ? (
              <Alert color="orange" title="Inactive">
                This practitioner is deactivated. Supervision and role permissions are read-only.
                Reactivation restores login access once roles are reassigned; caseload and historical
                AccessPolicy rows are not auto-restored.
              </Alert>
            ) : null}
            <Group>
              {!isInactive && !heldRoles.includes('practice-owner') && onDeactivate ? (
                <Button
                  color="red"
                  variant="light"
                  loading={accountActionLoading}
                  onClick={onDeactivate}
                >
                  Deactivate user
                </Button>
              ) : null}
              {isInactive && onReactivate ? (
                <Button color="teal" loading={accountActionLoading} onClick={onReactivate}>
                  Reactivate user
                </Button>
              ) : null}
            </Group>
            {heldRoles.includes('practice-owner') && !isInactive ? (
              <Alert color="blue">
                Practice owners cannot be deactivated directly. Transfer ownership in Practice
                settings first.
              </Alert>
            ) : null}
          </Stack>
        </Card>
      ) : null}

      <Modal
        opened={addRoleOpen}
        onClose={closeAddRoleModal}
        title="Add role"
      >
        <Stack gap="md">
          <Select
            label="Role"
            placeholder="Select a role"
            data={addOptions}
            value={roleToAdd}
            onChange={setRoleToAdd}
            searchable
            clearable
          />
          <ZoomSeatCapacityAlert
            selectedRoles={roleToAdd ? [roleToAdd] : []}
            usage={zoomSeatUsage.usage}
            loading={zoomSeatUsage.loading}
            error={zoomSeatUsage.error}
          />
          <Text size="xs" c="dimmed">
            Adding a clinical role keeps existing caseload untouched. Applies immediately.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeAddRoleModal}>
              Cancel
            </Button>
            <Button color="gold" disabled={!roleToAdd || zoomSeatsUnavailable} onClick={handleAdd}>
              Add role
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
